import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useGallery } from '@/hooks/useGallery';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { photoThumbnail } from '@/lib/api';
import { EVENT_NAME, EVENT_DATE, EVENT_SUBTITLE } from '@/lib/supabase';
import GalleryToggle, { type GalleryView } from '@/components/gallery/GalleryToggle';
import MagazineGallery from '@/components/gallery/MagazineGallery';
import MasonryGallery from '@/components/gallery/MasonryGallery';
import FeedGallery from '@/components/gallery/FeedGallery';
import type { Photo } from '@/types';

function getImageUrl(photo: Photo): string {
  if (photo.static_url) return photo.static_url;
  return photoThumbnail(photo);
}

/**
 * Hero photo — prima imagine din „revistă".
 * Sursa: /public/larisa si dani.jpg
 * Marcheaza cu is_hero=true ca sa nu primeasca like/view/comment.
 */
const HERO_PHOTO: Photo = {
  id: '__hero_cover__',
  event_slug: 'larisa-si-dani',
  uploader_name: 'Album de nuntă',
  uploader_uuid: '',
  width: null,
  height: null,
  size_bytes: null,
  status: 'approved',
  uploaded_at: Date.now(),
  caption: null,
  likes_count: 0,
  comments_count: 0,
  static_url: '/larisa%20si%20dani.jpg',
  is_hero: true,
};

const ADMIN_BAR_H = 56; // sincronizat cu AdminBar.tsx (h-14)

export default function GalleryPage() {
  const { data: photos = [], isLoading } = useGallery();
  const { role } = useAdminAuth();
  const [view, setView] = useState<GalleryView>('magazine');

  const hasAdminBar = role !== null;

  // Hero photo apare DOAR in Magazine view (ca prima pagina a revistei).
  // In Masonry / Feed NU apare — utilizatorii vad doar pozele reale din DB.
  const allPhotos: Photo[] =
    view === 'magazine' && !photos.some((p) => p.id === HERO_PHOTO.id)
      ? [HERO_PHOTO, ...photos]
      : photos;

  // Inaltimea disponibila pentru magazine = 100dvh minus AdminBar (daca exista)
  const availH = hasAdminBar ? `calc(100dvh - ${ADMIN_BAR_H}px)` : '100dvh';

  return (
    <div
      className={
        view === 'magazine'
          ? 'flex flex-col bg-transparent'
          : 'min-h-screen bg-transparent'
      }
      style={
        view === 'magazine'
          ? {
              height: availH,
              maxHeight: availH,
            }
          : undefined
      }
    >
      <header
        className={
          view === 'magazine'
            ? 'z-30 shrink-0 border-b border-border/50 bg-bg'
            : hasAdminBar
            ? `sticky top-[${ADMIN_BAR_H}px] z-30 border-b border-border/50 bg-bg`
            : 'sticky top-0 z-30 border-b border-border/50 bg-bg'
        }
      >
        <div className="container-page flex items-center justify-between py-md">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-fg-soft hover:text-fg">
            <span aria-hidden>←</span> Acasă
          </Link>
          <GalleryToggle value={view} onChange={setView} />
        </div>
      </header>

      <main className={view === 'magazine' ? 'flex-1 min-h-0 overflow-hidden' : 'py-md'}>
        {isLoading && photos.length === 0 ? (
          <div className="container-page grid grid-cols-2 gap-md sm:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton aspect-square" />
            ))}
          </div>
        ) : !photos.length ? (
          <div className="container-page py-2xl text-center">
            <p className="eyebrow text-xs uppercase tracking-widest text-fg-muted">
              {EVENT_DATE}
            </p>
            <h2 className="mt-md font-serif">{EVENT_NAME}</h2>
            <p className="mt-md text-fg-soft">{EVENT_SUBTITLE}</p>
            <p className="mt-md text-fg-soft">
              Încă nu sunt poze aprobate. Fii primul care încarcă una!
            </p>
            <Link to="/upload" className="btn-primary mt-lg">
              Încarcă o poză
            </Link>
          </div>
        ) : view === 'magazine' ? (
          <MagazineGallery
            key={`mag-${allPhotos.length}-${allPhotos[0]?.id ?? ''}-${allPhotos[0]?.uploader_name ?? ''}`}
            photos={allPhotos}
            mockImageFn={getImageUrl}
            eventName={EVENT_NAME}
            eventDate={EVENT_DATE}
            fillParent
          />
        ) : view === 'masonry' ? (
          <MasonryGallery photos={allPhotos} mockImageFn={getImageUrl} />
        ) : (
          <FeedGallery photos={allPhotos} mockImageFn={getImageUrl} />
        )}
      </main>
    </div>
  );
}